import { useEffect, useState } from 'react';
import { ClipLoader } from 'react-spinners';
import api from '../../api/api';

// Neighborhood view for one ICD-10-CM code: ancestor breadcrumb,
// instructional notes, siblings/children with expanders, and the
// billable-descendant ladder for non-billable headers. Presentational
// beyond its own fetch: selection and navigation are delegated up.
export default function CodeHierarchyBrowser({ code, onNavigate, onSelect }) {
    const [neighborhood, setNeighborhood] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        api.get(`/icd/code/${encodeURIComponent(code)}`)
            .then(resp => {
                if (!cancelled) setNeighborhood(resp.data);
            })
            .catch(() => {
                if (!cancelled) setError('Could not load this code.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [code]);

    if (loading) {
        return (
            <div className="code-browser-loading">
                <ClipLoader size={16} /> Loading {code}&hellip;
            </div>
        );
    }
    if (error || !neighborhood) {
        return <div className="code-browser-error">{error || 'Could not load this code.'}</div>;
    }

    const { code: node, ancestors, siblings, children, billable_descendants: descendants, notes } = neighborhood;
    const excludes1Notes = notes.filter(n => n.note_type === 'excludes1');
    const otherNotes = notes.filter(n => n.note_type !== 'excludes1');

    const renderRow = (row) => (
        <div key={row.code} className="code-browser-row">
            <span className="code-browser-row-code">{row.code}</span>
            <span className="code-browser-row-desc">{row.description}</span>
            <span className={`code-badge ${row.billable ? 'code-badge--billable' : 'code-badge--header'}`}>
                {row.billable ? 'Billable' : 'Header'}
            </span>
            <span className="code-browser-row-actions">
                {(row.has_children || !row.billable) && (
                    <button className="code-browser-btn" onClick={() => onNavigate(row.code)}>
                        Open
                    </button>
                )}
                {row.billable && (
                    <button
                        className="code-browser-btn code-browser-btn--select"
                        onClick={() => onSelect(row.code)}
                    >
                        Select
                    </button>
                )}
            </span>
        </div>
    );

    return (
        <div className="code-browser">
            <div className="code-browser-breadcrumb">
                {ancestors.map(ancestor => (
                    <span key={ancestor.code}>
                        <button
                            className="code-browser-crumb"
                            onClick={() => onNavigate(ancestor.code)}
                            title={ancestor.description}
                        >
                            {ancestor.code}
                        </button>
                        {' › '}
                    </span>
                ))}
                <span className="code-browser-crumb-current">{node.code}</span>
            </div>

            <div className="code-browser-node">
                <span className="code-browser-node-code">{node.code}</span>
                <span className="code-browser-node-desc">{node.description}</span>
                <span className={`code-badge ${node.billable ? 'code-badge--billable' : 'code-badge--header'}`}>
                    {node.billable ? 'Billable' : 'Header'}
                </span>
                {node.billable && (
                    <button
                        className="code-browser-btn code-browser-btn--select"
                        onClick={() => onSelect(node.code)}
                    >
                        Select this code
                    </button>
                )}
            </div>

            {excludes1Notes.length > 0 && (
                <div className="code-browser-notes code-browser-notes--excludes1">
                    {excludes1Notes.map((note, index) => (
                        <div key={index}>&#9888; Excludes1: {note.note_text}</div>
                    ))}
                </div>
            )}
            {otherNotes.length > 0 && (
                <details className="code-browser-notes">
                    <summary>Coding notes ({otherNotes.length})</summary>
                    {otherNotes.map((note, index) => (
                        <div key={index} className="code-browser-note">
                            <span className="code-browser-note-type">{note.note_type}:</span>{' '}
                            {note.note_text}
                        </div>
                    ))}
                </details>
            )}

            {children.length > 0 && (
                <div className="code-browser-section">
                    <div className="code-browser-section-title">More specific</div>
                    {children.map(renderRow)}
                </div>
            )}
            {!node.billable && children.length === 0 && descendants.length > 0 && (
                <div className="code-browser-section">
                    <div className="code-browser-section-title">Billable codes under {node.code}</div>
                    {descendants.map(renderRow)}
                </div>
            )}
            {siblings.length > 0 && (
                <div className="code-browser-section">
                    <div className="code-browser-section-title">Related (same parent)</div>
                    {siblings.map(renderRow)}
                </div>
            )}
        </div>
    );
}
